import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Car,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  MapPin,
  UserCheck,
  FileText,
  Download,
  X,
  Phone,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelCabBooking = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cabs, setCabs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCab, setEditCab] = useState(null);
  const [workflowCab, setWorkflowCab] = useState(null);
  const [driverDetailCab, setDriverDetailCab] = useState(null);
  const [deleteCab, setDeleteCab] = useState(null);

  // Form State
  const initialForm = {
    employee_name: '',
    department: 'General',
    cost_center: 'CC-101',
    travel_request_ref: '',
    service_type: 'Airport Pickup',
    pickup_location: '',
    drop_location: '',
    pickup_date: new Date().toISOString().split('T')[0],
    pickup_time: '09:00 AM',
    passenger_count: 1,
    vendor_name: 'Ola Corporate Cabs',
    vehicle_type: 'Sedan (Dzire)',
    estimated_cost: 1500,
    driver_name: '',
    driver_phone: '',
    vehicle_number: '',
    remarks: '',
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchCabs();
  }, [statusFilter, serviceTypeFilter]);

  const fetchCabs = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/travel-desk/cabs?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (serviceTypeFilter !== 'all') url += `service_type=${serviceTypeFilter}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;

      const { data } = await axios.get(url, { withCredentials: true });
      setCabs(data.cabs || []);
    } catch (err) {
      toast.error('Failed to load cab bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCab = async (e) => {
    e.preventDefault();
    if (!formData.pickup_location || !formData.drop_location) {
      toast.error('Please enter pickup and drop locations');
      return;
    }

    try {
      if (editCab) {
        await axios.put(`${API_URL}/travel-desk/cabs/${editCab._id}`, formData, { withCredentials: true });
        toast.success('Cab booking updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/cabs`, formData, { withCredentials: true });
        toast.success('New Cab booking created successfully!');
      }
      setShowCreateModal(false);
      setEditCab(null);
      setFormData(initialForm);
      fetchCabs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save cab booking');
    }
  };

  const handleWorkflowAction = async (cabId, action, extraPayload = {}) => {
    try {
      await axios.post(
        `${API_URL}/travel-desk/cabs/${cabId}/action`,
        { action, ...extraPayload },
        { withCredentials: true }
      );
      toast.success(`Action '${action}' processed successfully`);
      setWorkflowCab(null);
      fetchCabs();
    } catch (err) {
      toast.error('Failed to update cab workflow stage');
    }
  };

  const handleDeleteCab = async (cabId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/cabs/${cabId}`, { withCredentials: true });
      toast.success('Cab booking soft deleted');
      setDeleteCab(null);
      fetchCabs();
    } catch (err) {
      toast.error('Failed to delete cab booking');
    }
  };

  const openEdit = (cab) => {
    setEditCab(cab);
    setFormData({
      employee_name: cab.employee_name || '',
      department: cab.department || 'General',
      cost_center: cab.cost_center || '',
      travel_request_ref: cab.travel_request_ref || '',
      service_type: cab.service_type || 'Airport Pickup',
      pickup_location: cab.pickup_location || '',
      drop_location: cab.drop_location || '',
      pickup_date: cab.pickup_date || '',
      pickup_time: cab.pickup_time || '',
      passenger_count: cab.passenger_count || 1,
      vendor_name: cab.vendor_name || 'Ola Corporate Cabs',
      vehicle_type: cab.vehicle_type || 'Sedan',
      estimated_cost: cab.estimated_cost || 0,
      driver_name: cab.driver_name || '',
      driver_phone: cab.driver_phone || '',
      vehicle_number: cab.vehicle_number || '',
      remarks: cab.remarks || '',
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Booking Ref', 'Employee', 'Department', 'Service Type', 'Pickup', 'Drop', 'Pickup Date', 'Driver', 'Vehicle', 'Vendor', 'Cost', 'Status'];
    const rows = filteredCabs.map(c => [
      c.booking_ref, c.employee_name, c.department, c.service_type,
      `"${c.pickup_location}"`, `"${c.drop_location}"`, c.pickup_date,
      c.driver_name, c.vehicle_number, c.vendor_name, c.estimated_cost, c.status
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Cab_Bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredCabs = cabs.filter((c) => {
    const q = searchTerm.toLowerCase();
    return (
      (c.booking_ref || '').toLowerCase().includes(q) ||
      (c.employee_name || '').toLowerCase().includes(q) ||
      (c.pickup_location || '').toLowerCase().includes(q) ||
      (c.drop_location || '').toLowerCase().includes(q) ||
      (c.driver_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Car className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Cab &amp; Local Ground Booking</h1>
            <p className="text-xs text-blue-200">
              Airport Pickups, Airport Drops, Office Shuttles &amp; Vendor Fleet Assignments.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.cab_booking.edit') && (
            <button
              onClick={() => {
                setEditCab(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Book New Cab
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {['Airport Pickup', 'Airport Drop', 'Office Cab', 'Local Travel'].map((t) => (
          <div key={t} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-1">
            <div className="text-slate-400 text-xs font-bold uppercase">{t}</div>
            <div className="text-2xl font-black text-slate-900">
              {cabs.filter((c) => c.service_type === t).length} Bookings
            </div>
          </div>
        ))}
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search ref #, employee, location, driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold">
            <Filter className="w-3.5 h-3.5" /> Status:
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="assigned">Assigned</option>
            <option value="confirmed">Confirmed</option>
            <option value="trip_completed">Trip Completed</option>
            <option value="invoice_linked">Invoice Linked</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold ml-2">
            Service:
          </div>
          <select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Service Types</option>
            <option value="Airport Pickup">Airport Pickup</option>
            <option value="Airport Drop">Airport Drop</option>
            <option value="Office Cab">Office Cab</option>
            <option value="Local Travel">Local Travel</option>
          </select>

          <button
            onClick={fetchCabs}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex justify-between">
          <span>Cab Booking &amp; Fleet Roster ({filteredCabs.length})</span>
          <span className="text-slate-400">Live Database Master</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading cab bookings...</div>
        ) : filteredCabs.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No cab bookings found matching your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5">Booking Ref</th>
                  <th className="p-3.5">Service Type</th>
                  <th className="p-3.5">Employee &amp; Dept</th>
                  <th className="p-3.5">Pickup &amp; Drop</th>
                  <th className="p-3.5">Pickup Time</th>
                  <th className="p-3.5">Driver &amp; Vehicle</th>
                  <th className="p-3.5">Vendor</th>
                  <th className="p-3.5">Est. Cost</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredCabs.map((c) => (
                  <tr key={c._id} className="hover:bg-slate-50">
                    <td className="p-3.5 font-mono font-bold text-blue-600">{c.booking_ref}</td>
                    <td className="p-3.5 font-bold">{c.service_type}</td>
                    <td className="p-3.5 font-bold">
                      <div>{c.employee_name}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{c.department}</div>
                    </td>
                    <td className="p-3.5">
                      <div><b>From:</b> {c.pickup_location}</div>
                      <div className="text-slate-500"><b>To:</b> {c.drop_location}</div>
                    </td>
                    <td className="p-3.5 font-semibold text-slate-700">
                      <div>{c.pickup_date}</div>
                      <div className="text-[10px] text-slate-500">{c.pickup_time}</div>
                    </td>
                    <td className="p-3.5">
                      {c.driver_name && c.driver_name !== 'TBD' ? (
                        <div>
                          <div className="font-bold">{c.driver_name}</div>
                          <div className="font-mono text-slate-500">{c.vehicle_number} · {c.vehicle_type}</div>
                        </div>
                      ) : (
                        <span className="text-amber-600 italic font-semibold">TBD (Unassigned)</span>
                      )}
                    </td>
                    <td className="p-3.5">{c.vendor_name}</td>
                    <td className="p-3.5 font-mono font-bold text-emerald-700">
                      ₹ {(c.estimated_cost || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase ${
                        c.status === 'confirmed' || c.status === 'closed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : c.status === 'trip_completed' || c.status === 'invoice_linked'
                          ? 'bg-blue-100 text-blue-800'
                          : c.status === 'cancelled'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {c.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-3.5 text-right space-x-1 shrink-0">
                      <button
                        onClick={() => setDriverDetailCab(c)}
                        className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-bold text-[11px]"
                        title="View Driver & Vendor Details"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => setWorkflowCab(c)}
                        className="p-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-bold text-[11px]"
                        title="Workflow Progression"
                      >
                        Workflow
                      </button>
                      {hasPermission(user, 'travel_desk.cab_booking.edit') && (
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg font-bold text-[11px]"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteCab(c)}
                        className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg font-bold text-[11px]"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Car className="w-5 h-5 text-amber-500" />
                {editCab ? `Edit Cab Booking (${editCab.booking_ref})` : 'Book New Cab Requisition'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCab} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Passenger / Employee Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.employee_name}
                    onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="Rahul Sharma"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Service Type *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-bold"
                  >
                    <option value="Airport Pickup">Airport Pickup</option>
                    <option value="Airport Drop">Airport Drop</option>
                    <option value="Office Cab">Office Cab</option>
                    <option value="Local Travel">Local Travel</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Passenger Count</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.passenger_count}
                    onChange={(e) => setFormData({ ...formData, passenger_count: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Travel Request Ref</label>
                  <input
                    type="text"
                    placeholder="TRV-20260801-XXXX"
                    value={formData.travel_request_ref}
                    onChange={(e) => setFormData({ ...formData, travel_request_ref: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Pickup Location *</label>
                  <input
                    type="text"
                    required
                    placeholder="Bengaluru Airport (BLR)"
                    value={formData.pickup_location}
                    onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Drop Location *</label>
                  <input
                    type="text"
                    required
                    placeholder="Whitefield Campus"
                    value={formData.drop_location}
                    onChange={(e) => setFormData({ ...formData, drop_location: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Pickup Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.pickup_date}
                    onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-semibold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Pickup Time</label>
                  <input
                    type="text"
                    placeholder="09:30 AM"
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Estimated Cost (₹)</label>
                  <input
                    type="number"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vendor Partner</label>
                  <select
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  >
                    <option value="Ola Corporate Cabs">Ola Corporate Cabs</option>
                    <option value="Uber for Business">Uber for Business</option>
                    <option value="Savaari Car Rentals">Savaari Car Rentals</option>
                    <option value="Local Fleet Partner">Local Fleet Partner</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vehicle Type</label>
                  <select
                    value={formData.vehicle_type}
                    onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  >
                    <option value="Sedan (Dzire)">Sedan (Dzire / Etios)</option>
                    <option value="SUV (Innova)">SUV (Innova / Crysta)</option>
                    <option value="Hatchback (WagonR)">Hatchback (WagonR)</option>
                    <option value="Executive Luxury">Executive Luxury (BMW/Audi)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Driver Name (Optional)</label>
                  <input
                    type="text"
                    placeholder="Suresh Kumar"
                    value={formData.driver_name}
                    onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Driver Mobile</label>
                  <input
                    type="text"
                    placeholder="+91 9876543210"
                    value={formData.driver_phone}
                    onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vehicle Plate #</label>
                  <input
                    type="text"
                    placeholder="KA-01-MJ-4890"
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Remarks &amp; Special Instructions</label>
                <textarea
                  rows="2"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl outline-none text-xs"
                  placeholder="e.g. Flight arrives at Terminal 2, driver should carry placard."
                />
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editCab ? 'Save Changes' : 'Confirm Cab Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WORKFLOW ACTION MODAL */}
      {workflowCab && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">
                Workflow Actions — {workflowCab.booking_ref}
              </h3>
              <button onClick={() => setWorkflowCab(null)} className="text-slate-400 font-bold">&times;</button>
            </div>
            <p className="text-xs text-slate-600">
              Current Stage: <b className="uppercase text-blue-600">{workflowCab.status?.replace(/_/g, ' ')}</b>
            </p>

            <div className="space-y-2 pt-2">
              <button
                onClick={() => handleWorkflowAction(workflowCab._id, 'approve')}
                className="w-full py-2 bg-emerald-600 text-white font-bold text-xs rounded-xl shadow-sm hover:bg-emerald-500"
              >
                1. Manager Approve (Move to Assigned)
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowCab._id, 'assign_driver', { driver_name: 'Rajesh Kumar', driver_phone: '+91 9876543210', vehicle_number: 'KA-01-AB-1234' })}
                className="w-full py-2 bg-blue-600 text-white font-bold text-xs rounded-xl shadow-sm hover:bg-blue-500"
              >
                2. Confirm Fleet &amp; Driver Assignment
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowCab._id, 'complete_trip')}
                className="w-full py-2 bg-indigo-600 text-white font-bold text-xs rounded-xl shadow-sm hover:bg-indigo-500"
              >
                3. Mark Trip Completed
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowCab._id, 'close')}
                className="w-full py-2 bg-slate-800 text-white font-bold text-xs rounded-xl shadow-sm hover:bg-slate-700"
              >
                4. Close Booking Entry
              </button>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setWorkflowCab(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DRIVER & VENDOR DETAIL DRAWER */}
      {driverDetailCab && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" /> Fleet &amp; Driver Details
              </h3>
              <button onClick={() => setDriverDetailCab(null)} className="text-slate-400 font-bold">&times;</button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-slate-50 p-3 rounded-xl space-y-1">
                <div className="text-slate-400 font-bold uppercase text-[10px]">Booking Reference</div>
                <div className="font-mono text-sm font-bold text-blue-600">{driverDetailCab.booking_ref}</div>
              </div>

              <div className="grid grid-cols-2 gap-2 bg-blue-50/50 p-3 rounded-xl border border-blue-100">
                <div>
                  <span className="text-slate-500 font-medium block">Driver Name</span>
                  <span className="font-bold text-slate-900">{driverDetailCab.driver_name || 'Unassigned'}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">Driver Phone</span>
                  <span className="font-mono font-bold text-blue-700">{driverDetailCab.driver_phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">Vehicle Plate No</span>
                  <span className="font-mono font-bold text-slate-900">{driverDetailCab.vehicle_number || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500 font-medium block">Vehicle Model</span>
                  <span className="font-bold text-slate-900">{driverDetailCab.vehicle_type || 'N/A'}</span>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl space-y-1">
                <span className="text-slate-400 font-bold uppercase text-[10px]">Vendor Fleet Partner</span>
                <div className="font-bold text-slate-900">{driverDetailCab.vendor_name}</div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl space-y-1">
                <span className="text-slate-400 font-bold uppercase text-[10px]">Remarks</span>
                <div className="text-slate-700">{driverDetailCab.remarks || 'None'}</div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setDriverDetailCab(null)} className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOFT DELETE CONFIRM MODAL */}
      {deleteCab && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Soft Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete Cab Booking <b className="font-mono text-slate-900">{deleteCab.booking_ref}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleteCab(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeleteCab(deleteCab._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelCabBooking;
